import { Component, Input } from '@angular/core';
import { NgClass } from '@angular/common';
import { ListQueryComponent } from 'components/query/list-query.component';
import { InputType } from 'enums/InputType';
import { QueryType } from 'enums/QueryType';
import { TaskActionType } from 'enums/TaskActionType';
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { DynamicQueryModel, SelectPropertyModel, AggregationEnum } from 'models/generated/Models.DynamicQuery';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { QueryPropertyModel, QueryPropertyType, QueryPropertyValueType } from 'models/generated/Models.QueryProperty';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Inputs } from 'modules/inputs';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { QueryService, QueryResults } from 'services/query.service';
import { RelationService } from 'services/relation.service';
import { QueryInputComponent } from './query-input.component';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';

interface IQueryFilter extends ExpressionFilterModel {
	queryFilterValue?: string;
}

@Component({
	selector: 'editquery',
	templateUrl: './edit-query.component.html',
	providers: [FormValidationService],
	imports: [Layout, Inputs, QueryInputComponent, ListView, TranslatePipe, NgClass]
})
@ComponentUrl('edit-query')
export class EditQueryComponent {

	loading = true;

	public title = '';
	public query = new QueryModel();
	showTab: 'filters' | 'selectProperties' | 'run' = 'filters';

	inputTypeEnum = InputType;
	queryTypeEnum = QueryType;
	taskActionTypeEnum = TaskActionType;
	aggregationTypeList: SelectListItemModel[];
	queryTypeList: SelectListItemModel[] = [];
	_queryData = new DynamicQueryModel();
	set queryData(data: DynamicQueryModel) {
		this._queryData = data;
	}
	get queryData() { return this._queryData; }

	queryProperties?: QueryPropertyModel[];
	queryPropertiesList: SelectListItemModel[] = [];
	queryType?: QueryType;

	selectPropertiesList: SelectListItemModel[] = [];

	operatorType = Comparison;
	queryPropertyType = QueryPropertyType;
	queryPropertyValueType = QueryPropertyValueType;
	results?: QueryResults[];
	validFilters: IQueryFilter[] = [];

	@UrlParam
	@Input()
	set queryId(id: string | undefined) {
		if (id != undefined) {
			this.loading = true;
			this.queryService.getQuery(id).then(query => {
				this.query = query;
				this.loadQuery();
			});
		} else {
			this.loadQuery();
		}
	}
	get queryId() {
		return this.query && this.query.id ? this.query.id : '';
	}

	constructor(
		private readonly navigation: NavigationService,
		readonly queryService: QueryService,
		private readonly translate: TranslatePipe) {

		this.queryTypeList = [];
		let queryTypeList: SelectListItemModel[] = [];
		QueryType.getKeys().forEach((value: string, key: QueryType) => {
			queryTypeList.push(new SelectListItemModel(key, this.translate.transform('enum.query-type.' + value)));
		});
		this.queryTypeList = queryTypeList;
		this.aggregationTypeList = [];
		let aggregationTypeList: SelectListItemModel[] = [];
		AggregationEnum.getKeys().forEach((value: string, key: AggregationEnum) => {
			aggregationTypeList.push(new SelectListItemModel(key, this.translate.transform('enum.aggregation-type.' + value)));
		});
		this.aggregationTypeList = aggregationTypeList;
	}

	availableFormatTypes(property: SelectPropertyModel): SelectListItemModel[] {
		let formatTypeList: SelectListItemModel[] = [];
		if (property.type == 'boolean')
			formatTypeList.push(new SelectListItemModel('boolean', this.translate.transform('query.format.boolean')));
		if (property.type == 'number' || property.type == 'currency') {
			formatTypeList.push(new SelectListItemModel('number', this.translate.transform('query.format.number')));
			formatTypeList.push(new SelectListItemModel('currency', this.translate.transform('query.format.currency')));
		}
		if (property.type == 'Date' || property.type == 'shortDate') {
			formatTypeList.push(new SelectListItemModel('Date', this.translate.transform('query.format.date')));
			formatTypeList.push(new SelectListItemModel('shortDate', this.translate.transform('query.format.shortDate')));
		}
		if (property.type == 'guid' || property.type == 'user' || property.type == 'team') {
			formatTypeList.push(new SelectListItemModel('guid', this.translate.transform('query.format.guid')));
			formatTypeList.push(new SelectListItemModel('user', this.translate.transform('query.format.user')));
			formatTypeList.push(new SelectListItemModel('team', this.translate.transform('query.format.team')));
		}
		if (formatTypeList.length == 0)
			formatTypeList.push(new SelectListItemModel('string', this.translate.transform('query.format.string')));
		return formatTypeList;
	}

	async loadQuery() {
		this.loading = true;
		this.title = this.translate.transform(this.query.id ? 'query.edit-query' : 'query.create-query');
		this.queryData = new DynamicQueryModel();
		if (this.query.data != undefined && this.query.data != '') {
			this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), Object.assign(this.queryData, JSON.parse(this.query.data)));
			this.queryType = this.queryData.queryType;
		} else {
			this.resetFilter();
		}
		await this.loadProperties();
		this.loading = false;
		this.navigation.refreshUrl();
		this.navigation.openParentView(this, 'list-query', ListQueryComponent)
			.then(view => { view.selectedQuery = this.query; });
	}

	async loadProperties() {
		if (this.queryData && this.queryType != undefined) {
			if (this.queryData.queryType == undefined) this.queryData.queryType = this.queryType;
			if (!this.query.id) this.resetFilter();

			this.selectPropertiesList = this.queryService.getPropertyDetails(this.queryType, this.queryData.selectProperties).map(p => { return new SelectListItemModel(p.derivedName, this.getPropertyLabel(p.derivedName, p.languageString!)); });

			if (this.queryData.selectProperties.length == 0) {
				const properties = this.queryService.getPropertyDetails(this.queryType);
				this.queryData.selectProperties = properties.filter(p => p.derivedName.toLowerCase().indexOf('id') == -1 && p.derivedName.toLowerCase().indexOf('delete') == -1 && p.derivedName.indexOf('.') == -1).map(p => {
					let property = new SelectPropertyModel()
					this.queryService.setSelectProperty(property, p, false);
					return property;
				});
			}

			const properties = await this.queryService.getQueryProperties(this.queryType);
			this.queryProperties = properties;
			this.queryPropertiesList = [];
			properties.forEach(property => {
				this.queryPropertiesList.push(new SelectListItemModel(property.name, this.translate.transform('query.' + this.queryService.getPropertyLanguageString(property.name, QueryType[this.queryType!]))));
			});
			this.queryData.filters.forEach(filter => {
				this.setFilterOperatorsList(filter);
			});
		}
	}

	getPropertyLabel(databaseString: string, languageString: string): string {
		return (databaseString.indexOf('.') != -1 ? this.translate.transform('query.' + databaseString.split('.')[databaseString.split('.').length - 2].toLowerCase()) + ' > ' : '') + this.translate.transform('query.' + languageString);
	}

	addSelectProperty() {
		this.queryData.selectProperties.push(new SelectPropertyModel());
	}

	removeSelectProperty(property: SelectPropertyModel) {
		if (this.queryData.selectProperties.length == 1) return;
		const index = this.queryData.selectProperties.findIndex(p => p == property);
		this.queryData.selectProperties.splice(index, 1);
	}

	setSelectProperty(property: SelectPropertyModel, propertyName: string) {
		const properties = this.queryService.getPropertyDetails(this.queryType!, this.queryData.selectProperties);
		const isHidden = property?.isHidden ?? false;
		const aggregationType = property?.aggregationType;
		const newProperty = properties.find(p => p.derivedName == propertyName);
		if (newProperty)
			this.queryService.setSelectProperty(property, newProperty, isHidden, aggregationType);
	}

	resetFilter() {
		this.queryData.filters = [];
		this.addFilter();
	}

	addFilter() {
		const filter = new ExpressionFilterModel();
		filter.operatorsList = [];
		this.queryData.filters.push(filter);
	}
	setFilterProperty(filter: ExpressionFilterModel, propertyName: string) {
		filter.property = this.queryProperties!.find(qp => qp.name == propertyName);
		this.setFilterOperatorsList(filter);
	}

	setFilterOperatorsList(filter: ExpressionFilterModel) {
		if (!this.queryProperties) return;
		let property = this.queryProperties.find(p => p.name === filter.property?.name);
		if (!property) return;
		let operatorsList: SelectListItemModel[] = [];
		let comparisonsList: Comparison[] = [];
		if (property.type == QueryPropertyType.Value) {
			switch (property.valueType) {
				case QueryPropertyValueType.Text:
					comparisonsList.push(Comparison.Equal);
					comparisonsList.push(Comparison.Contains);
					comparisonsList.push(Comparison.StartsWith);
					comparisonsList.push(Comparison.EndsWith);
					break;
				case QueryPropertyValueType.Numeric:
				case QueryPropertyValueType.Date:
				case QueryPropertyValueType.DateTime:
				case QueryPropertyValueType.Time:
					comparisonsList.push(Comparison.Equal);
					comparisonsList.push(Comparison.NotEqual);
					comparisonsList.push(Comparison.GreaterThan);
					comparisonsList.push(Comparison.GreaterThanOrEqual);
					comparisonsList.push(Comparison.LessThan);
					comparisonsList.push(Comparison.LessThanOrEqual);
					break;
				default:
					comparisonsList.push(Comparison.Equal);
					break;
			}
		}
		else if (property.type == QueryPropertyType.Query) {
			comparisonsList.push(Comparison.Equal);
			comparisonsList.push(Comparison.In);
			comparisonsList.push(Comparison.NotIn);
		}
		else {
			comparisonsList.push(Comparison.Equal);
		}
		if (property.nullable) {
			comparisonsList.push(Comparison.NotNull);
			comparisonsList.push(Comparison.Null);
		}
		
		comparisonsList.forEach(comparison => {
			operatorsList.push(new SelectListItemModel(comparison.toString(), this.translate.transform('query.operator.' + Comparison[comparison])));
		});
		
		filter.operatorsList = operatorsList.map(item => Object.assign(new SelectListItemModel(item.value, item.name), item));
		this.validFilters = this.queryData.filters.filter(f => f.property).map(f => Object.assign(new ExpressionFilterModel(), f) as IQueryFilter).map(f => Object.assign(f, { queryFilterValue: f.value }));
	}

	removeFilter(filter: ExpressionFilterModel) {
		if (this.queryData.filters.length == 1) return;
		const index = this.queryData.filters.findIndex(f => f == filter);
		this.queryData.filters.splice(index, 1);
	}

	saveQuery() {
		if (!this.queryData || this.queryType == undefined) return;
		this.queryData.queryType = this.queryType!;
		
		this.queryService.saveQuery(this.query, this.queryData).then(query => {
			this.navigation.refreshView(EditQueryComponent).then(view => view.queryId = query.id ? query.id : '');
		});
	}

	async runQuery() {
		this.queryData.queryType = this.queryType!;
		this.results = undefined;
		// clone queryData to avoid changes to original object
		let queryData: DynamicQueryModel = RelationService.instantiateModelsFromData(new DynamicQueryModel(), RelationService.deepClone(this.queryData));
		let filters: ExpressionFilterModel[] = [];
		this.validFilters.forEach(f => {
			let filter = RelationService.instantiateModelsFromData(new ExpressionFilterModel(), RelationService.deepClone(f));
			filter.value = filter.queryFilterValue;
			filters.push(filter);
		});
		queryData.filters = filters;
		switch (queryData.queryType) {
			case QueryType.TaskAction:
				this.results = await this.queryService.executeFilterQuery<TaskActionModel[]>(queryData);
				break;
			case QueryType.Taskflow:
				this.results = await this.queryService.executeFilterQuery<TaskflowModel[]>(queryData);
				break;
			case QueryType.Company:
				this.results = await this.queryService.executeFilterQuery<CompanyModel[]>(queryData);
				break;
			case QueryType.Order:
				this.results = await this.queryService.executeFilterQuery<OrderModel[]>(queryData);
				break;
			case QueryType.Product:
				this.results = await this.queryService.executeFilterQuery<ProductModel[]>(queryData);
				break;
			case QueryType.ProductPlan:
				this.results = await this.queryService.executeFilterQuery<ProductPlanModel[]>(queryData);
				break;
		}
	}

	get visibleSelectProperties() {
		return this.queryData.selectProperties.filter(property => !property.isHidden);
	}

	getOptions(filter: ExpressionFilterModel): { [key: string]: string } {
		if (!this.queryProperties) return {};
		var queryProperty = this.queryProperties.find(queryProperty => queryProperty.name == filter.property?.name);
		if (!queryProperty) return {};
		return queryProperty.options;
	}

	isNullOperator(comparison: Comparison): boolean {
		return (comparison == Comparison.Null || comparison == Comparison.NotNull);
	}

	canSearch() {
		if (this.queryData.filters.some(filter => filter.property == undefined || filter.property?.name == ''))
			return false;
		return true;
	}

}
